import { useInfiniteQuery } from '@tanstack/react-query';
import { FC, useMemo, useState } from 'react';
import styled from 'styled-components';

import InfiniteLoader from '@/components/InfiniteLoader';
import { SrOnly } from '@/components/Layout/Parts';
import PulsingLoader from '@/components/PulsingLoader';
import ScrollableTable from '@/components/ScrollableTable';
import SearchBar from '@/components/SearchBar';
import { Table, TableContent, Th } from '@/components/Table';
import Text from '@/components/Text';
import useDebounce from '@/hooks/useDebounce';
import { PaginatedResponse, VideoCompilerCompilation } from '@/types/resources';
import { api } from '@/utils/api';
import { VideoCompilerCompilationStatus } from '@/utils/enums';

import { ActiveTab } from '..';
import CompilationsTableRow from './CompilationsTableRow';

const StyledTable = styled(Table)`
  td,
  th {
    vertical-align: top;
    font-weight: 700;

    &:first-child {
      padding-left: 24px;
      /* width: 129px; */
    }

    /* &:nth-child(2) {
      width: 232px;
    }

    &:nth-child(3) {
      width: 232px;
    }

    &:nth-child(4) {
      width: 140px;
    }

    &:nth-child(5) {
      width: 100px;
    }

    &:nth-child(6) {
      width: 110px;
    }

    &:nth-child(7) {
      width: 160px;
    }

    &:nth-child(8) {
      width: 200px;
    } */

    &:last-child {
      padding-right: 24px;
    }
  }
`;

const Card = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  flex-direction: column;
  justify-content: center;
  background: white;
  border-radius: 4px;
  padding: 120px 0;
`;

const EmptyPlaceholderText = styled(Text)`
  display: flex;
  gap: 8px;
  align-items: center;
  margin: 0 0 16px;
`;

const CreateNewButton = styled(Text)`
  width: 125px;
  height: 38px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 100px;
  border: 1px solid ${({ theme }) => theme.colors.lightGray};
`;

interface Props {
  activeTab: ActiveTab;
  onCreateNew: VoidFunction;
}

const CompilationsTable: FC<Props> = ({ activeTab, onCreateNew }) => {
  const [search, setSearch] = useState('');

  const debouncedSearch = useDebounce(search, 500);

  const fetchData = ({ pageParam = '/v1/video-compiler/compilations' }) => {
    return api.get<never, PaginatedResponse<VideoCompilerCompilation>>(
      pageParam,
      {
        params: {
          'filter[status]': activeTab.status,
          ...(!!debouncedSearch && { 'filter[search]': debouncedSearch }),
        },
      },
    );
  };

  const {
    data,
    isError,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useInfiniteQuery(
    [
      'video_compiler_compilations',
      {
        status: activeTab.status,
        ...(!!debouncedSearch && { search: debouncedSearch }),
      },
    ],
    fetchData,
    {
      getNextPageParam: (lastPage) => {
        return lastPage.links.next;
      },
      staleTime: 60 * 1000,
      refetchInterval: 30_000,
    },
  );

  const compilations = useMemo(
    () =>
      data?.pages?.reduce<VideoCompilerCompilation[]>((acc, curr) => {
        return [...acc, ...curr.data];
      }, []),
    [data],
  );

  if (
    activeTab.status === VideoCompilerCompilationStatus.IN_PROCESS &&
    isLoading
  ) {
    return (
      <Card>
        <PulsingLoader />
      </Card>
    );
  }

  if (
    activeTab.status === VideoCompilerCompilationStatus.IN_PROCESS &&
    !compilations?.length
  ) {
    return (
      <Card>
        <EmptyPlaceholderText $type="button" $color="gray">
          <svg
            width="20"
            height="21"
            viewBox="0 0 20 21"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M7.40649 7.16675H13.3324"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M7.40649 10.1297H13.3324"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M7.40649 13.0927H10.3695"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M13.025 19.0185H2.96144C2.76499 19.0185 2.57658 18.9369 2.43766 18.7917C2.29875 18.6464 2.2207 18.4495 2.2207 18.2441V2.75586C2.2207 2.55047 2.29875 2.3535 2.43766 2.20827C2.57658 2.06303 2.76499 1.98145 2.96144 1.98145H17.7763C17.9727 1.98145 18.1611 2.06303 18.3 2.20827C18.439 2.3535 18.517 2.55047 18.517 2.75586V13.2768C18.517 13.3785 18.4978 13.4792 18.4606 13.5732C18.4234 13.6671 18.3688 13.7525 18.3 13.8244L13.5488 18.7917C13.48 18.8636 13.3983 18.9206 13.3085 18.9595C13.2186 18.9985 13.1223 19.0185 13.025 19.0185V19.0185Z"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M18.4502 13.0918H13.332V18.2103"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          No activity yet
        </EmptyPlaceholderText>
        <CreateNewButton
          as="button"
          $type="captionSemibold"
          onClick={onCreateNew}
        >
          Create new
        </CreateNewButton>
      </Card>
    );
  }

  return (
    <div>
      <SearchBar
        value={search}
        setValue={setSearch}
        placeholder="Search by Project ID"
      />
      <ScrollableTable>
        <StyledTable $stickyActions>
          <thead>
            <tr>
              <Th>Title</Th>
              <Th>Destination</Th>
              <Th>Type</Th>
              <Th>Status</Th>
              <Th>Versions</Th>
              <Th>Tips</Th>
              <Th>Channels</Th>
              <Th>Created By</Th>
              <Th $actionsCol>
                <SrOnly>Actions</SrOnly>
              </Th>
            </tr>
          </thead>
          <tbody>
            <TableContent
              isLoading={isLoading}
              isError={isError}
              hasData={!!compilations?.length}
            >
              {compilations?.map((compilation) => (
                <CompilationsTableRow
                  key={compilation.id}
                  compilation={compilation}
                />
              ))}
            </TableContent>
          </tbody>
        </StyledTable>
      </ScrollableTable>
      <InfiniteLoader
        hasNextPage={hasNextPage}
        fetchNextPage={fetchNextPage}
        isFetchingNextPage={isFetchingNextPage}
      />
    </div>
  );
};

export default CompilationsTable;
